// Feature #374 — Skill Requests queue (sm-admin).
//
// Users lodge requests for missing technical skills from inside an
// assessment; this is where a Skills Administrator accepts or rejects them.
// Accepting lands the skill in the catalogue (the admin may tidy the name
// and add a description first — catalogue quality stays with the admin);
// rejecting keeps the row and sends the note to the requester. Both notify
// the requester in-app.
//
// Server-paged like the other list views (#337): the queue is worked in
// arrival order, so open requests come oldest-first.
import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Modal } from '@/components/ui/Modal'
import {
  acceptSkillRequest,
  getSkillRequests,
  rejectSkillRequest,
  SmSkillRequest,
  SmSkillRequestStatus,
} from '@/api/sm'

const PAGE_SIZE = 20

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  width: '100%',
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-dark-grey)',
  display: 'block',
  marginBottom: '6px',
}

const buttonStyle: React.CSSProperties = {
  padding: '6px 14px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButton: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-magenta)',
  border: '1px solid var(--color-magenta)',
}

const STATUS_LABEL: Record<SmSkillRequestStatus, string> = {
  in_review: 'In review',
  accepted: 'Accepted',
  rejected: 'Rejected',
}

const STATUS_STYLE: Record<SmSkillRequestStatus, React.CSSProperties> = {
  in_review: { backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' },
  accepted: { backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7' },
  rejected: { backgroundColor: '#f8fafc', color: 'var(--color-dark-grey)', border: '1px solid var(--color-light-grey)' },
}

const StatusPill: React.FC<{ status: SmSkillRequestStatus }> = ({ status }) => (
  <span
    style={{
      ...STATUS_STYLE[status],
      fontSize: '12px',
      fontWeight: 500,
      borderRadius: '10px',
      padding: '1px 9px',
      lineHeight: '18px',
      whiteSpace: 'nowrap',
    }}
  >
    {STATUS_LABEL[status]}
  </span>
)

type Decision = { kind: 'accept' | 'reject'; request: SmSkillRequest }

const SkillRequests: React.FC = () => {
  // Default to the work: the open queue.
  const [status, setStatus] = useState<SmSkillRequestStatus | 'all'>('in_review')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<SmSkillRequest[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [decision, setDecision] = useState<Decision | null>(null)
  // Fields of the decision dialog.
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    getSkillRequests({
      status: status === 'all' ? undefined : status,
      page,
      page_size: PAGE_SIZE,
    })
      .then((result) => {
        setItems(result.items)
        setTotal(result.total)
        setTotalPages(Math.max(1, result.total_pages))
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [status, page]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [status])

  const open = (kind: Decision['kind'], request: SmSkillRequest) => {
    setDecision({ kind, request })
    setName(request.name)
    setDescription('')
    setNote('')
    setDecisionError(null)
  }

  const close = () => {
    if (submitting) return
    setDecision(null)
  }

  const decide = async () => {
    if (!decision) return
    setDecisionError(null)
    if (decision.kind === 'accept' && !name.trim()) {
      setDecisionError('The skill needs a name.')
      return
    }
    setSubmitting(true)
    try {
      if (decision.kind === 'accept') {
        await acceptSkillRequest(decision.request.id, {
          name: name.trim() !== decision.request.name ? name.trim() : undefined,
          description: description.trim() || undefined,
          note: note.trim() || undefined,
        })
      } else {
        await rejectSkillRequest(decision.request.id, { note: note.trim() || undefined })
      }
      setDecision(null)
      load()
    } catch (e: any) {
      setDecisionError(e?.response?.data?.detail || e?.message || 'Could not save the decision')
    } finally {
      setSubmitting(false)
    }
  }

  const dirty =
    !!decision &&
    (name.trim() !== decision.request.name || description.trim() !== '' || note.trim() !== '')

  const startRecord = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endRecord = Math.min(page * PAGE_SIZE, total)

  return (
    <div>
      <PageHeader title="Skill Requests" />
      <div style={{ padding: '24px', display: 'grid', gap: '24px', maxWidth: '1100px' }}>
        <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: 0 }}>
          Skills people asked for while filling in an assessment. Accepting adds the
          skill to the catalogue and tells the requester it is available; rejecting
          keeps the request on record and tells them why. Nothing reaches the
          catalogue until you say so.
        </p>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              {status === 'all' ? 'All requests' : STATUS_LABEL[status]}{' '}
              <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--color-dark-grey)' }}>
                {total} request{total === 1 ? '' : 's'}
              </span>
            </h2>
            <div role="tablist" aria-label="Filter by state" style={{ display: 'flex', gap: '6px' }}>
              {(['in_review', 'accepted', 'rejected', 'all'] as const).map((s) => (
                <button
                  key={s}
                  role="tab"
                  aria-selected={status === s}
                  type="button"
                  onClick={() => setStatus(s)}
                  style={{
                    ...secondaryButton,
                    backgroundColor: status === s ? 'var(--color-magenta)' : 'transparent',
                    color: status === s ? 'var(--color-white)' : 'var(--color-magenta)',
                  }}
                >
                  {s === 'all' ? 'All' : STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {loading && <p style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>Loading…</p>}
          {!loading && items.length === 0 && (
            <p style={{ fontSize: '14px', color: 'var(--color-dark-grey)' }}>
              {status === 'in_review'
                ? 'Nothing waiting for review.'
                : 'No requests here yet.'}
            </p>
          )}

          <div style={{ display: 'grid', gap: '8px' }}>
            {items.map((r) => (
              <div
                key={r.id}
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '8px',
                  display: 'grid',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '15px' }}>{r.name}</span>
                  <StatusPill status={r.status} />
                  <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                    {r.requester_display_name || 'Unknown'} ·{' '}
                    {new Date(r.created_at).toLocaleDateString()}
                    {r.subject_display_name &&
                      ` · while assessing ${
                        r.subject_user_id === r.requester_user_id ? 'themselves' : r.subject_display_name
                      }`}
                  </span>
                  {r.status === 'in_review' && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                      <button type="button" style={buttonStyle} onClick={() => open('accept', r)}>
                        Accept
                      </button>
                      <button type="button" style={secondaryButton} onClick={() => open('reject', r)}>
                        Reject
                      </button>
                    </span>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {r.justification}
                </p>
                {r.status !== 'in_review' && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                    {STATUS_LABEL[r.status]} by {r.resolved_by_display_name || 'Unknown'}
                    {r.resolved_at && ` on ${new Date(r.resolved_at).toLocaleDateString()}`}
                    {r.status === 'accepted' &&
                      r.created_skill_name &&
                      r.created_skill_name !== r.name &&
                      ` as “${r.created_skill_name}”`}
                    {r.resolution_note && ` — “${r.resolution_note}”`}
                  </p>
                )}
              </div>
            ))}
          </div>

          {total > PAGE_SIZE && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '12px',
                fontSize: '13px',
                color: 'var(--color-dark-grey)',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span>
                Showing {startRecord} to {endRecord} of {total}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  style={{ ...secondaryButton, opacity: page === 1 ? 0.5 : 1 }}
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  style={{ ...secondaryButton, opacity: page === totalPages ? 0.5 : 1 }}
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <Modal
        isOpen={decision !== null}
        onClose={close}
        title={
          decision?.kind === 'accept'
            ? `Accept “${decision.request.name}”`
            : `Reject “${decision?.request.name ?? ''}”`
        }
        size="md"
        isDirty={dirty}
        preventClose={submitting}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" style={{ ...secondaryButton, padding: '9px 20px' }} onClick={close} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...buttonStyle, padding: '9px 20px', cursor: submitting ? 'wait' : 'pointer' }}
              onClick={decide}
              disabled={submitting}
            >
              {submitting
                ? 'Saving…'
                : decision?.kind === 'accept'
                  ? 'Add to catalogue'
                  : 'Reject request'}
            </button>
          </div>
        }
      >
        {decision && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div
              style={{
                padding: '10px 12px',
                backgroundColor: '#f8fafc',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
            >
              <strong>{decision.request.requester_display_name || 'The requester'}</strong> wrote:{' '}
              <span style={{ whiteSpace: 'pre-wrap' }}>{decision.request.justification}</span>
            </div>
            {decisionError && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#b91c1c',
                  fontSize: '13px',
                }}
              >
                {decisionError}
              </div>
            )}
            {decision.kind === 'accept' && (
              <>
                <div>
                  <label htmlFor="skill-request-accept-name" style={labelStyle}>
                    Catalogue name
                  </label>
                  <input
                    id="skill-request-accept-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={255}
                    style={inputStyle}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: '6px 0 0' }}>
                    Tidy the wording if needed — the request keeps what was asked for.
                    If an active skill already has this name, the request is linked to it
                    rather than creating a duplicate.
                  </p>
                </div>
                <div>
                  <label htmlFor="skill-request-accept-description" style={labelStyle}>
                    Description (optional)
                  </label>
                  <input
                    id="skill-request-accept-description"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={2000}
                    placeholder="Shown under the skill on every assessment form"
                    style={inputStyle}
                  />
                </div>
              </>
            )}
            <div>
              <label htmlFor="skill-request-note" style={labelStyle}>
                {decision.kind === 'accept' ? 'Note to the requester (optional)' : 'Reason (optional)'}
              </label>
              <textarea
                id="skill-request-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder={
                  decision.kind === 'accept'
                    ? 'Goes into their notification'
                    : 'e.g. already covered by “Infrastructure as Code” — goes into their notification'
                }
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default SkillRequests
